import * as XLSX from 'xlsx'
import { readSafeWorkbook, validateExcelPayload, validateWorkbookBytes } from '@/lib/safeWorkbook'
import { dataUrlToArrayBuffer } from '@/lib/files'

export type WoodRow = {
  stt: string
  name: string
  woodType: string
  thick: string
  width: string
  length: string
  tenon: string
  qty: string
  qtyTotal: string
  note: string
  veneer: string
  m3: string
}

export type HardwareRow = {
  stt: string
  name: string
  spec: string
  qty: string
  qtyTotal: string
  unit: string
  color: string
  kind: string
  note: string
}

export type HardwareTable = {
  /** Resolved title, e.g. "VẬT TƯ SẢN XUẤT- MP069" from formula & E6 */
  title: string
  /** Suffix pulled from title formula ref (E6 → MP069), if any */
  code: string
  rows: HardwareRow[]
}

export type BomDocument = {
  productCode: string
  title: string
  woodTitle: string
  wood: WoodRow[]
  /** Flattened for convenience; prefer hardwareTables for UI */
  hardware: HardwareRow[]
  hardwareTables: HardwareTable[]
  woodPartCount: number
  woodTotalM3: number
}

type Matrix = unknown[][]

function cellStr(value: unknown): string {
  if (value == null || value === '') return ''
  if (value instanceof Date) return value.toISOString().slice(0, 10)
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return ''
    return Number.isInteger(value) ? String(value) : String(value)
  }
  return String(value).replace(/\s+/g, ' ').trim()
}

function cellDisplay(value: unknown): string {
  const raw = cellStr(value)
  if (!raw) return ''
  if (typeof value === 'number' && !Number.isInteger(value)) {
    const rounded = Math.round(value * 1000) / 1000
    return String(rounded)
  }
  return raw
}

function cellNum(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const n = Number(String(value ?? '').replace(',', '.').replace(/[^\d.-]/g, ''))
  return Number.isFinite(n) ? n : 0
}

function norm(text: string): string {
  return text
    .normalize('NFC')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

function rowJoin(row: unknown[]): string {
  return (row ?? []).map((c) => cellStr(c)).join(' | ')
}

function looksLike(text: string, ...needles: string[]): boolean {
  const n = norm(text)
  return needles.some((needle) => n.includes(norm(needle)))
}

function sheetToMatrix(ws: XLSX.WorkSheet): Matrix {
  return XLSX.utils.sheet_to_json(ws, {
    header: 1,
    defval: '',
    raw: true,
    blankrows: true,
  }) as Matrix
}

function padStt(value: string, fallback: number): string {
  const n = Number(value)
  if (Number.isFinite(n) && n > 0) return String(n).padStart(2, '0')
  return String(fallback).padStart(2, '0')
}

function findCol(header: unknown[], ...needles: string[]): number {
  const idx = header.findIndex((cell) => looksLike(cellStr(cell), ...needles))
  return idx
}

function isSubHeaderRow(row: unknown[]): boolean {
  const text = rowJoin(row)
  return looksLike(text, 'dày') && looksLike(text, 'rộng') && looksLike(text, 'dài')
}

function isWoodHeader(row: unknown[]): boolean {
  const text = rowJoin(row)
  return (
    looksLike(text, 'tên chi tiết') &&
    (looksLike(text, 'loại gỗ') || looksLike(text, 'loai go'))
  )
}

function isHardwareHeader(row: unknown[]): boolean {
  const text = rowJoin(row)
  return (
    looksLike(text, 'tên vật tư') &&
    (looksLike(text, 'quy cách') || looksLike(text, 'số lượng'))
  )
}

function isHardwareTitleRow(row: unknown[]): boolean {
  return looksLike(rowJoin(row), 'vật tư sản xuất')
}

function isWoodTitleRow(row: unknown[]): boolean {
  const text = rowJoin(row)
  return looksLike(text, 'định mức sản xuất') && !looksLike(text, 'vật tư')
}

function isFooterRow(row: unknown[]): boolean {
  const text = rowJoin(row)
  return looksLike(text, 'người lập', 'người kiểm tra')
}

function isSectionBreak(row: unknown[]): boolean {
  return (
    isHardwareTitleRow(row) ||
    isHardwareHeader(row) ||
    isWoodHeader(row) ||
    isFooterRow(row)
  )
}

/** Resolve titles like `="VẬT TƯ SẢN XUẤT- " & E6` by reading the referenced cell. */
function resolveTitleFromCell(
  ws: XLSX.WorkSheet,
  rowIndex0: number,
  colIndex0 = 0,
): { title: string; code: string } {
  const addr = XLSX.utils.encode_cell({ r: rowIndex0, c: colIndex0 })
  const cell = ws[addr] as XLSX.CellObject | undefined
  const cached = cellStr(cell?.w ?? cell?.v)

  if (cell?.f) {
    const formula = String(cell.f).replace(/^\s*=/, '').trim()
    const concat = formula.match(/^"([^"]*)"\s*&\s*([A-Za-z]+\d+)\s*$/)
    if (concat) {
      const prefix = concat[1]
      const ref = concat[2].toUpperCase()
      const refCell = ws[ref] as XLSX.CellObject | undefined
      const suffix = cellStr(refCell?.w ?? refCell?.v)
      const title = `${prefix}${suffix}`.replace(/\s+/g, ' ').trim()
      const codeMatch = suffix.match(/[A-Z]{1,4}\d{2,}/i)
      return {
        title: title || cached,
        code: codeMatch?.[0]?.toUpperCase() ?? '',
      }
    }
  }

  const codeMatch = cached.match(/[A-Z]{1,4}\d{2,}/i)
  return {
    title: cached,
    code: codeMatch?.[0]?.toUpperCase() ?? '',
  }
}

function findTitleAbove(
  ws: XLSX.WorkSheet,
  matrix: Matrix,
  headerRow0: number,
  kind: 'hardware' | 'wood',
): { title: string; code: string; row0: number } {
  for (let r = headerRow0 - 1; r >= Math.max(0, headerRow0 - 6); r -= 1) {
    const row = matrix[r] ?? []
    const match =
      kind === 'hardware' ? isHardwareTitleRow(row) : isWoodTitleRow(row)
    if (!match) continue
    // Prefer column A (merged title bar), else first non-empty cell
    let col = 0
    if (!cellStr(row[0])) {
      const idx = row.findIndex((c) => cellStr(c))
      if (idx >= 0) col = idx
    }
    const resolved = resolveTitleFromCell(ws, r, col)
    return { ...resolved, row0: r }
  }
  return { title: '', code: '', row0: -1 }
}

function extractMeta(
  wb: XLSX.WorkBook,
  matrices: Matrix[],
): { productCode: string; title: string; woodTitle: string } {
  let productCode = ''
  let title = ''
  let woodTitle = ''

  for (let s = 0; s < matrices.length; s += 1) {
    const matrix = matrices[s]
    const ws = wb.Sheets[wb.SheetNames[s]]
    for (let r = 0; r < Math.min(matrix.length, 40); r += 1) {
      const row = matrix[r] ?? []
      const text = rowJoin(row)

      if (!woodTitle && isWoodTitleRow(row)) {
        const resolved = resolveTitleFromCell(ws, r, 0)
        woodTitle = resolved.title
        if (resolved.code) productCode = resolved.code
      }

      if (!title && looksLike(text, 'định mức')) {
        title = cellStr(row.find((c) => looksLike(cellStr(c), 'định mức')))
      }

      // Prefer header code cell E6 / D2 style
      for (let c = 0; c < Math.min(row.length, 8); c += 1) {
        const v = cellStr(row[c])
        if (/^MP\d+/i.test(v) && !productCode) productCode = v.toUpperCase()
      }

      const named = text.match(/định mức sản xuất\s*-\s*([A-Z0-9]+)/i)
      if (named?.[1]) {
        productCode = named[1].toUpperCase()
        title = title || `Định mức sản xuất - ${productCode}`
      }
    }
  }

  // Explicit E6 / D2 if still missing
  if (!productCode) {
    for (const name of wb.SheetNames) {
      const ws = wb.Sheets[name]
      for (const addr of ['E6', 'D2', 'E2']) {
        const cell = ws[addr] as XLSX.CellObject | undefined
        const v = cellStr(cell?.w ?? cell?.v)
        if (/^MP\d+/i.test(v)) {
          productCode = v.toUpperCase()
          break
        }
      }
      if (productCode) break
    }
  }

  return {
    productCode,
    woodTitle:
      woodTitle ||
      (productCode ? `ĐỊNH MỨC SẢN XUẤT - ${productCode}` : 'ĐỊNH MỨC SẢN XUẤT'),
    title:
      title ||
      (productCode
        ? `Định mức nguyên vật liệu - ${productCode}`
        : 'Định mức nguyên vật liệu'),
  }
}

function parseWoodFromMatrix(
  ws: XLSX.WorkSheet,
  matrix: Matrix,
): { rows: WoodRow[]; totalM3: number; title: string; code: string } {
  const headerIdx = matrix.findIndex(isWoodHeader)
  if (headerIdx < 0) return { rows: [], totalM3: 0, title: '', code: '' }

  const titleInfo = findTitleAbove(ws, matrix, headerIdx, 'wood')
  const header = matrix[headerIdx] ?? []
  let dataStart = headerIdx + 1
  if (isSubHeaderRow(matrix[dataStart] ?? [])) dataStart += 1
  if (isWoodHeader(matrix[dataStart] ?? [])) dataStart += 1
  if (isSubHeaderRow(matrix[dataStart] ?? [])) dataStart += 1

  const nameIdx = Math.max(findCol(header, 'tên chi tiết'), 1)
  const typeIdx = Math.max(findCol(header, 'loại gỗ', 'loai go'), nameIdx + 1)
  const tenonIdx = findCol(header, 'mộng', 'tenon')
  const thickIdx = typeIdx + 1
  const widthIdx = typeIdx + 2
  const lengthIdx = typeIdx + 3
  const m3Idx = (() => {
    const tinh = header.findIndex((c) => looksLike(cellStr(c), 'm3 tinh', 'm³ tinh'))
    if (tinh >= 0) return tinh
    return header.findIndex((c) => looksLike(cellStr(c), 'm3', 'm³'))
  })()
  const veneerIdx = findCol(header, 'veneer', 'quy cách veneer')
  const noteIdx = findCol(header, 'ghi chú')
  const qtyIdx = header.findIndex((c) => {
    const t = norm(cellStr(c))
    return (
      t === 'sl' ||
      t === 'sl (1 sp)' ||
      (t.startsWith('sl') && !t.includes('tổng') && !t.includes('đơn'))
    )
  })
  const qtyTotalIdx = header.findIndex((c) =>
    looksLike(cellStr(c), 'sl tổng', 'sl đơn hàng', 'sl tong'),
  )

  const rows: WoodRow[] = []
  let totalM3 = 0
  for (let i = dataStart; i < matrix.length; i += 1) {
    const row = matrix[i] ?? []
    if (isSectionBreak(row) || isHardwareTitleRow(row)) break
    const name = cellStr(row[nameIdx])
    if (!name) continue
    if (looksLike(name, 'tổng', 'stt', 'tên chi tiết')) continue
    const qty = cellDisplay(row[qtyIdx >= 0 ? qtyIdx : 10])
    const qtyTotal = cellDisplay(row[qtyTotalIdx >= 0 ? qtyTotalIdx : 11])
    if (!qty && !qtyTotal && !cellStr(row[typeIdx])) continue
    const m3Value = m3Idx >= 0 ? cellNum(row[m3Idx]) : 0

    rows.push({
      stt: padStt(cellStr(row[0]), rows.length + 1),
      name,
      woodType: cellStr(row[typeIdx]),
      thick: cellDisplay(row[thickIdx]),
      width: cellDisplay(row[widthIdx]),
      length: cellDisplay(row[lengthIdx]),
      tenon: cellDisplay(row[tenonIdx >= 0 ? tenonIdx : typeIdx + 4]),
      qty: qty || '0',
      qtyTotal: qtyTotal || qty || '0',
      note: noteIdx >= 0 ? cellStr(row[noteIdx]) : '',
      veneer: veneerIdx >= 0 ? cellStr(row[veneerIdx]) : '',
      m3: m3Value ? m3Value.toFixed(3) : '',
    })
    totalM3 += m3Value
  }
  return {
    rows,
    totalM3,
    title: titleInfo.title,
    code: titleInfo.code,
  }
}

function parseHardwareRows(
  matrix: Matrix,
  headerIdx: number,
): HardwareRow[] {
  const header = matrix[headerIdx] ?? []
  const nameIdx = Math.max(findCol(header, 'tên vật tư'), 1)
  const specIdx = Math.max(findCol(header, 'quy cách'), nameIdx + 1)
  const qtyIdx = header.findIndex((c) => {
    const t = norm(cellStr(c))
    return t === 'số lượng' || t === 'so luong' || t === 'sl'
  })
  const qtyTotalIdx = findCol(header, 'sl tổng', 'sl tong')
  const unitIdx = findCol(header, 'đơn vị', 'don vi')
  const colorIdx = findCol(header, 'màu', 'mau')
  const kindIdx = findCol(header, 'loại', 'phan loại')
  const noteIdx = findCol(header, 'ghi chú', 'vị trí')

  const rows: HardwareRow[] = []
  for (let i = headerIdx + 1; i < matrix.length; i += 1) {
    const row = matrix[i] ?? []
    // Stop at next hardware block / footer — do NOT merge multiple tables
    if (
      isHardwareTitleRow(row) ||
      isHardwareHeader(row) ||
      isWoodHeader(row) ||
      isFooterRow(row)
    ) {
      break
    }
    const name = cellStr(row[nameIdx])
    if (!name) continue
    if (looksLike(name, 'stt', 'tên vật tư')) continue
    const qty = cellDisplay(row[qtyIdx >= 0 ? qtyIdx : specIdx + 1])
    const qtyTotal = cellDisplay(row[qtyTotalIdx >= 0 ? qtyTotalIdx : specIdx + 2])
    rows.push({
      stt: padStt(cellStr(row[0]), rows.length + 1),
      name,
      spec: cellStr(row[specIdx]),
      qty: qty || '0',
      qtyTotal: qtyTotal || qty || '0',
      unit: unitIdx >= 0 ? cellStr(row[unitIdx]) : '',
      color: colorIdx >= 0 ? cellStr(row[colorIdx]) : '',
      kind: kindIdx >= 0 ? cellStr(row[kindIdx]) : '',
      note: noteIdx >= 0 ? cellStr(row[noteIdx]) : '',
    })
  }
  return rows
}

function parseHardwareTablesFromSheet(
  ws: XLSX.WorkSheet,
  matrix: Matrix,
): HardwareTable[] {
  const tables: HardwareTable[] = []
  for (let r = 0; r < matrix.length; r += 1) {
    if (!isHardwareHeader(matrix[r] ?? [])) continue
    const titleInfo = findTitleAbove(ws, matrix, r, 'hardware')
    const rows = parseHardwareRows(matrix, r)
    if (!rows.length && !titleInfo.title) continue
    const fallbackTitle = titleInfo.title || 'VẬT TƯ SẢN XUẤT'
    tables.push({
      title: fallbackTitle,
      code: titleInfo.code,
      rows,
    })
  }
  return tables
}

function parseHardwareSheet2(matrix: Matrix): HardwareRow[] {
  const rows: HardwareRow[] = []
  for (const row of matrix) {
    const name = cellStr(row[2] || row[1])
    const spec = cellStr(row[3] || row[2])
    if (!name) continue
    if (looksLike(name, 'tên', 'stt')) continue
    const hasQtyOrSpec =
      cellStr(row[4]) !== '' ||
      cellStr(row[6]) !== '' ||
      /[0-9x×Ø]/.test(spec) ||
      looksLike(name, 'vít', 'vis', 'ray', 'tay nắm', 'bulong', 'đinh', 'guzong', 'sò')
    if (!hasQtyOrSpec && !cellStr(row[6])) continue
    const qty = cellDisplay(row[4] ?? row[3])
    rows.push({
      stt: padStt('', rows.length + 1),
      name,
      spec: cellStr(row[3]) || spec,
      qty: qty || '0',
      qtyTotal: qty || '0',
      unit: cellStr(row[6]),
      color: cellStr(row[7]),
      kind: cellStr(row[8]),
      note: cellStr(row[9]),
    })
  }
  return rows.filter((row) => row.name && (row.spec || row.unit || row.qty !== '0'))
}

export function parseBomWorkbook(data: ArrayBuffer | Uint8Array): BomDocument {
  const wb = readSafeWorkbook(data, { cellDates: true })
  const matrices = wb.SheetNames.map((name) => sheetToMatrix(wb.Sheets[name]))
  const meta = extractMeta(wb, matrices)

  let wood: WoodRow[] = []
  let woodTotalM3 = 0
  let woodTitle = meta.woodTitle
  const hardwareTables: HardwareTable[] = []

  for (let s = 0; s < matrices.length; s += 1) {
    const matrix = matrices[s]
    const ws = wb.Sheets[wb.SheetNames[s]]
    if (!wood.length) {
      const parsed = parseWoodFromMatrix(ws, matrix)
      wood = parsed.rows
      woodTotalM3 = parsed.totalM3
      if (parsed.title) woodTitle = parsed.title
      if (parsed.code && !meta.productCode) meta.productCode = parsed.code
    }
    hardwareTables.push(...parseHardwareTablesFromSheet(ws, matrix))
  }

  if (!hardwareTables.length && matrices[1]) {
    const fallback = parseHardwareSheet2(matrices[1])
    if (fallback.length) {
      hardwareTables.push({
        title: meta.productCode
          ? `VẬT TƯ SẢN XUẤT- ${meta.productCode}`
          : 'VẬT TƯ SẢN XUẤT',
        code: meta.productCode,
        rows: fallback,
      })
    }
  }

  const hardware = hardwareTables.flatMap((t) => t.rows)
  const woodPartCount = wood.reduce((sum, row) => sum + (Number(row.qty) || 0), 0)

  return {
    productCode: meta.productCode,
    title: meta.title,
    woodTitle,
    wood,
    hardware,
    hardwareTables,
    woodPartCount,
    woodTotalM3: Math.round(woodTotalM3 * 1000) / 1000,
  }
}

export async function parseBomFromDataUrl(dataUrl: string, fileName?: string): Promise<BomDocument> {
  const buffer = await dataUrlToArrayBuffer(dataUrl)
  if (fileName) {
    if (fileName.toLowerCase().endsWith('.csv')) {
      throw new Error('Định dạng BOM không hỗ trợ CSV. Vui lòng chọn file Excel.')
    }
    validateWorkbookBytes(fileName, new Uint8Array(buffer))
  } else {
    validateExcelPayload(new Uint8Array(buffer))
  }
  return parseBomWorkbook(buffer)
}
